// ============================================================================
// tenantApi.js — WARALS Tenant Provisioning ("hacker-panel") API layer
//
// Uses the SAME shared session keys AccountForm.jsx / LogIn.jsx already
// write for every other panel (SuperAdmin, Teacher, Student, Parent) — this
// is not a separate login, just a different destination after the same
// two-stage login:
//
//   sessionStorage.token1       -> Stage 1 token (account lookup)
//   sessionStorage.token        -> Stage 2 token (the real bearer, all panels)
//   sessionStorage.refreshToken -> refresh token paired with Token 2
//   sessionStorage.clientCode   -> ClientCode (needed for refresh)
//   sessionStorage.userRole     -> Role claim decoded from Token 2 — this
//                                  panel additionally requires it to equal
//                                  "SuperAdmin" (enforced in AdminLayout.jsx)
// ============================================================================

const BASE = `https://wsmsv1.aidigitalsec.com/api`;

function decodeJwt(token) {
  try {
    const clean = token.replace(/^Bearer\s+/i, '');
    const payload = clean.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return {};
  }
}

function getRoleFromToken(token) {
  const claims = decodeJwt(token);
  return (
    claims.role ||
    claims.Role ||
    claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ||
    null
  );
}

async function tryRefresh() {
  try {
    const clientCode = sessionStorage.getItem('clientCode');
    const refreshToken = sessionStorage.getItem('refreshToken');
    if (!clientCode || !refreshToken) return false;

    const res = await fetch(`${BASE}/UserLogin/refresh-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clientCode, refreshToken }),
    });
    if (!res.ok) return false;

    const data = await res.json();
    sessionStorage.setItem('token', data.token);
    sessionStorage.setItem('refreshToken', data.refreshToken);
    return true;
  } catch {
    return false;
  }
}

// Doc requires the literal header "Authorization: Bearer <Token>" for every
// protected call (Stage 2 login section + Table 0/3/8). Session storage holds
// the raw JWT with no prefix, so it must be added here — this was previously
// missing, which would make every authenticated call fail with 401.
function asBearer(token) {
  if (!token) return null;
  return /^Bearer\s+/i.test(token) ? token : `Bearer ${token}`;
}

async function request(path, { method = 'GET', body, auth = true, useToken1 = false } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (auth) {
    const token = useToken1
      ? sessionStorage.getItem('token1')
      : sessionStorage.getItem('token');
    const bearer = asBearer(token);
    if (bearer) headers.Authorization = bearer;
  }

  let res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  // Silent single-retry on 401 for Token2-protected calls
  if (res.status === 401 && auth && !useToken1) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      headers.Authorization = asBearer(sessionStorage.getItem('token'));
      res = await fetch(`${BASE}${path}`, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    }
  }

  let data = {};
  try {
    data = await res.json();
  } catch {
    /* empty body */
  }

  if (!res.ok) {
    const err = new Error(data.msg || data.message || `Request failed (${res.status})`);
    err.status = res.status;
    err.payload = data;
    throw err;
  }
  return data;
}

// Builds "?a=1&b=2" from an object, skipping null / undefined / '' values.
function qs(params) {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') sp.set(k, v);
  });
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export const auth = {
  lookupOrg: (accountName) =>
    request('/Default', { method: 'POST', body: { accountName }, auth: false }),

  login: (userId, password) =>
    request('/UserLogin', { method: 'POST', body: { userId, password }, useToken1: true }),

  refresh: tryRefresh,

  revokeOtherSessions: (clientCode, refreshToken) =>
    request('/UserLogin/v1/admin/tenants/revoke-tokens', {
      method: 'POST',
      body: { clientCode, refreshToken },
    }),

  decodeJwt,
  getRoleFromToken,
};

export const tenantApi = {
  // ---- Step 1: Database -----------------------------------------------
  createDatabase: (clientName) =>
    request('/v1/admin/tenants/database', { method: 'POST', body: { clientName } }),
  getDatabase: (clientName) =>
    request(`/v1/admin/tenants/database?clientName=${encodeURIComponent(clientName)}`),
  deleteDatabase: (clientName) =>
    request(`/v1/admin/tenants/database?clientName=${encodeURIComponent(clientName)}`, {
      method: 'DELETE',
    }),
  getAllDatabases: (page = 1, pageSize = 20) =>
    request(`/v1/admin/tenants/database/all?page=${page}&pageSize=${pageSize}`),

  // ---- Modules & Forms -------------------------------------------------
  // Confirmed against Module_Form_Permission_Sync_API_Documentation.docx:
  // GetModules, GetModuleById, CreateModule, UpdateModule, DeleteModule,
  // GetForms, GetFormById, CreateForm, UpdateForm, DeleteForm.
  //
  // Per instruction, this is plain CRUD only — organisationId/branchId are
  // passed in by the caller as static 0/0 (see ORG_ID/BRANCH_ID in
  // Modulemanagement.jsx / Formmanagement.jsx), with no tenant/org lookup.
  // Deliberately NOT included: SavePermission_SyncAll, GetPermissions
  // (handled on the separate Permissions page) and DeploySchema (not part
  // of this CRUD task).
  //
  // moduleId/formId are server-generated on create — omit them from
  // Create* bodies. UpdateForm expects updatedBy, not createdBy. DeleteForm
  // takes no body/query at all — the audit user comes from the Bearer token.
  getModules: (organisationId, branchId) =>
    request(`/ModuleForm/GetModules?organisationId=${organisationId}&branchId=${branchId}`),
  getModuleById: (moduleId) => request(`/ModuleForm/GetModuleById/${moduleId}`),
  createModule: (payload) =>
    request('/ModuleForm/CreateModule', { method: 'POST', body: payload }),
  updateModule: (moduleId, payload) =>
    request(`/ModuleForm/UpdateModule/${moduleId}`, { method: 'PUT', body: payload }),
  deleteModule: (moduleId) =>
    request(`/ModuleForm/DeleteModule/${moduleId}`, { method: 'DELETE' }),

  getForms: ({ moduleId, organisationId, branchId } = {}) => {
    const q = new URLSearchParams();
    if (moduleId != null) q.set('moduleId', moduleId);
    if (organisationId != null) q.set('organisationId', organisationId);
    if (branchId != null) q.set('branchId', branchId);
    return request(`/ModuleForm/GetForms?${q.toString()}`);
  },
  getFormById: (formId) => request(`/ModuleForm/GetFormById/${formId}`),
  createForm: (payload) =>
    request('/ModuleForm/CreateForm', { method: 'POST', body: payload }),
  updateForm: (formId, payload) =>
    request(`/ModuleForm/UpdateForm/${formId}`, { method: 'PUT', body: payload }),
  deleteForm: (formId) =>
    request(`/ModuleForm/DeleteForm/${formId}`, { method: 'DELETE' }),

  // ---- Step 2: Seed (Run / Status / Reset) --------------------------------
  runSeed: (tenantDbName) =>
    request('/v1/admin/tenants/seed', { method: 'POST', body: { tenantDbName } }),
  getSeedStatus: (tenantDbName) =>
    request(`/v1/admin/tenants/seed/status?tenantDbName=${encodeURIComponent(tenantDbName)}`),
  resetSeed: (tenantDbName) =>
    request(`/v1/admin/tenants/seed?tenantDbName=${encodeURIComponent(tenantDbName)}`, {
      method: 'DELETE',
    }),

  // ---- Step 3: Configure (Master DB registration) ------------------------
  registerTenant: (payload) =>
    request('/v1/admin/tenants/configure', { method: 'POST', body: payload }),
  getTenant: (clientCode) => request(`/v1/admin/tenants/configure/${clientCode}`),
  getAllTenants: () => request('/v1/admin/tenants/configure'),
  updateTenant: (clientCode, payload) =>
    request(`/v1/admin/tenants/configure/${clientCode}`, { method: 'PUT', body: payload }),
  deleteTenant: (clientCode) =>
    request(`/v1/admin/tenants/configure/${clientCode}`, { method: 'DELETE' }),

  // ---- Step 4: Admin credentials ------------------------------------------
  getAdminUser: (tenantDbName) =>
    request(`/v1/admin/tenants/admin-user?tenantDbName=${encodeURIComponent(tenantDbName)}`),
  updateAdminUser: (payload) =>
    request('/v1/admin/tenants/admin-user', { method: 'PUT', body: payload }),
  resetAdminUser: (tenantDbName) =>
    request(`/v1/admin/tenants/admin-user?tenantDbName=${encodeURIComponent(tenantDbName)}`, {
      method: 'DELETE',
    }),

  // ---- Permissions (CPanel) ------------------------------------------------
  // Replaces the old /v1/admin/tenants/admin-user/permissions revoke / restore
  // flow. Every call is scoped to a tenant via ?ClientCode=<tenant clientCode>
  // (e.g. "EXCE"), and the Stage-2 SuperAdmin bearer token is sent as usual.
  //
  // Dropdown sources (all are POST with query-string params, no body):
  getCpanelOrganizations: (clientCode) =>
    request(`/CPanel/GetOrganization${qs({ ClientCode: clientCode })}`, { method: 'POST' }),
  getCpanelBranches: (orgId, clientCode) =>
    request(`/CPanel/GetBranch${qs({ OrgId: orgId, ClientCode: clientCode })}`, { method: 'POST' }),
  getCpanelUserGroups: (clientCode) =>
    request(`/CPanel/GetUserGroup_admin${qs({ ClientCode: clientCode })}`, { method: 'POST' }),

  // Permission rows for one role. moduleId / formId are optional filters —
  // leave them out to get every module + form for that role.
  getCpanelPermissions: ({ orgId, branchId, userGroupId, moduleId, formId, clientCode }) =>
    request(
      `/CPanel/GetPermission${qs({
        OrgId: orgId,
        BranchId: branchId,
        userGroupId,
        moduleId,
        formId,
        ClientCode: clientCode,
      })}`
    ),
  getPermissionById: (userRoleId, clientCode) =>
    request(`/CPanel/GetPermissionById/${userRoleId}${qs({ ClientCode: clientCode })}`),

    // GET /api/ModuleForm/GetPermissions
  // qs() skips null / undefined / '' — so moduleId (All Module), formId and
  // userRoleId are simply left off the URL and the API receives them as null.
  getModulePermissions: ({ clientCode, userRoleId, organisationId, branchId, userGroupId, moduleId, formId } = {}) =>
    request(
      `/ModuleForm/GetPermissions${qs({
        clientCode,
        userRoleId,
        organisationId,
        branchId,
        userGroupId,
        moduleId,
        formId,
      })}`
    ),

  // Save one form.
  //   ctx  = { orgId, branchId, clientCode }
  //   body = { userGroupId, moduleId, formId, moduleName, formName,
  //            uRead, uWrite, uModify, uDelete, uPrint, uApproval }
  savePermission: ({ orgId, branchId, clientCode }, body) =>
    request(`/CPanel/SavePermission${qs({ OrgId: orgId, BranchId: branchId, ClientCode: clientCode })}`, {
      method: 'POST',
      body,
    }),

  // Save many forms of ONE module in a single call.
  //   body = { userGroupId, moduleId, permissions: [{ formId, uRead, ... uApproval }] }
  savePermissionBulk: ({ orgId, branchId, clientCode }, body) =>
    request(
      `/CPanel/SavePermissionBulk${qs({ OrgId: orgId, BranchId: branchId, ClientCode: clientCode })}`,
      { method: 'POST', body }
    ),

  removePermission: ({ orgId, branchId, userGroupId, formId, clientCode }) =>
    request(
      `/CPanel/RemovePermission${qs({
        OrgId: orgId,
        BranchId: branchId,
        UserGroupId: userGroupId,
        FormId: formId,
        ClientCode: clientCode,
      })}`,
      { method: 'DELETE' }
    ),

  // ---- Step 5: Organization profile -----------------------------------------
  setOrganization: (payload) =>
    request('/v1/admin/tenants/organization', { method: 'PUT', body: payload }),
  getOrganization: (tenantDbName) =>
    request(`/v1/admin/tenants/organization?tenantDbName=${encodeURIComponent(tenantDbName)}`),
  clearOrganization: (tenantDbName) =>
    request(`/v1/admin/tenants/organization?tenantDbName=${encodeURIComponent(tenantDbName)}`, {
      method: 'DELETE',
    }),

  // ---- Monitoring (read-only) --------------------------------------------------
  // Storage footprint for ONE client (database files, file folders, DB properties).
  // Used by the db_sizes page, which calls it once per selected client.
  // Response: { status: "Success", data: { summary, database, tables, files, databaseProperties } }
  getStorageInfo: (clientCode) =>
    request(`/AdminStorage/GetStorageInfo${qs({ ClientCode: clientCode })}`),

  // Legacy paged endpoint — no longer used by the db_sizes page, kept in case
  // another screen still calls it.
  getDbSizes: (page = 1, pageSize = 20) =>
    request(`/v1/admin/tenants/monitoring/db-sizes?page=${page}&pageSize=${pageSize}`),

  // ---- Logs (read-only) ---------------------------------------------------------

  // Raw login log for ONE client, server-side paginated. Used by login_dashboard.
  getLoginLogReport: (clientCode, page = 1, pageSize = 20) =>
    request(
      `/Default/LoginLogReportJson?clientCode=${encodeURIComponent(clientCode)}&page=${page}&pageSize=${pageSize}`
    ),

  getLoginHistory: (clientCode, page = 1, pageSize = 20) =>
    request(
      `/v1/admin/tenants/logs/login-history?clientCode=${encodeURIComponent(
        clientCode
      )}&page=${page}&pageSize=${pageSize}`
    ),
  getAuditLogs: (clientCode, page = 1, pageSize = 20) =>
    request(
      `/v1/admin/tenants/logs/audit?clientCode=${encodeURIComponent(
        clientCode
      )}&page=${page}&pageSize=${pageSize}`
    ),
  getExceptionLogs: (clientCode, fromDate = '', toDate = '', page = 1, pageSize = 20) =>
    request(
      `/v1/admin/tenants/logs/exceptions?clientCode=${encodeURIComponent(
        clientCode
      )}&fromDate=${fromDate}&toDate=${toDate}&page=${page}&pageSize=${pageSize}`
    ),
};